import { useEffect, useState, useCallback } from "react";
import { Bike, Footprints, Store, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PaymentMethodDialog, KIND_LABELS } from "@/components/sistema/PaymentMethodDialog";
import { MercadoPagoCard } from "@/components/sistema/MercadoPagoCard";
import { useAuth } from "@/context/AuthContext";
import { api, brl, errMsg } from "@/lib/api";
import { toast } from "sonner";

const KIND_ICONS = { delivery: Bike, retirada: Footprints, local: Store };
const feeText = (m) => (Number(m.fee_value) > 0 ? (m.fee_type === "pct" ? `${m.fee_value}%` : brl(m.fee_value)) : "Não informado");

function MethodRow({ m, onPatch, onEdit }) {
  return (
    <TableRow className={m.active ? "" : "opacity-60"} data-testid={`pay-method-row-${m.id}`}>
      <TableCell><Switch checked={m.active} onCheckedChange={(v) => onPatch({ active: v })} data-testid={`pay-method-toggle-${m.id}`} /></TableCell>
      <TableCell className="font-medium">{m.label}</TableCell>
      <TableCell><button onClick={() => onPatch({ menu: !m.menu })} className={`rounded-md px-2 py-1 text-xs font-semibold ${m.menu && m.active ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`} data-testid={`pay-method-menu-${m.id}`}>{m.menu ? "Habilitado" : "Desabilitado"}</button></TableCell>
      <TableCell>
        <div className="flex gap-1">{Object.keys(KIND_LABELS).map((k) => { const I = KIND_ICONS[k]; const on = m.order_types.includes(k); return (
          <button key={k} title={KIND_LABELS[k]} onClick={() => onPatch({ order_types: on ? m.order_types.filter((x) => x !== k) : [...m.order_types, k] })} className={`rounded-md p-1.5 ${on ? "text-foreground" : "text-muted-foreground/40"}`} data-testid={`pay-method-kind-${m.id}-${k}`}><I className="h-4 w-4" /></button>
        ); })}</div>
      </TableCell>
      <TableCell className="text-sm">{feeText(m)}</TableCell>
      <TableCell className="text-sm max-w-[160px] truncate">{m.notes || "Não"}</TableCell>
      <TableCell className="text-sm max-w-[160px] truncate">{m.brands.length ? m.brands.join(", ") : "Não informado"}</TableCell>
      <TableCell><Button size="sm" variant="ghost" onClick={onEdit} data-testid={`pay-method-edit-${m.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Editar</Button></TableCell>
    </TableRow>
  );
}

export function PaymentMethodsTab() {
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/payment-methods").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const save = async (methods) => {
    try {
      const { data: d } = await api.put("/payment-methods", { methods: Object.fromEntries(methods.map(({ id, label, ...cfg }) => [id, cfg])) });
      setData((x) => ({ ...x, methods: d.methods }));
      toast.success("Formas de pagamento salvas");
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const patch = (id, p) => save(data.methods.map((m) => (m.id === id ? { ...m, ...p } : m)));
  if (!data) return null;
  return (
    <div className="space-y-6">
      <div className="card-surface overflow-x-auto">
        <p className="border-b p-4 text-sm text-muted-foreground">As formas ativas valem para todos os caixas (PDV, comandas, mesas, encomendas e delivery). Na Venda ticket você ainda pode limitar por usuário em Usuários.</p>
        <Table data-testid="pay-methods-table">
          <TableHeader><TableRow><TableHead className="w-14" /><TableHead>Método</TableHead><TableHead>Cardápio digital</TableHead><TableHead>Tipo de pedido</TableHead><TableHead>Taxa extra</TableHead><TableHead>Obs.</TableHead><TableHead>Bandeiras</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>{data.methods.map((m) => <MethodRow key={m.id} m={m} onPatch={(p) => patch(m.id, p)} onEdit={() => setEdit(m)} />)}</TableBody>
        </Table>
      </div>
      <MercadoPagoCard mp={data.mercadopago} onSaved={(mp) => setData((x) => ({ ...x, mercadopago: mp }))} />
      <PaymentMethodDialog method={edit} brands={data.brands} onClose={() => setEdit(null)} onSave={(m) => { setEdit(null); patch(m.id, m); }} />
    </div>
  );
}
